import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Collapse,
} from '@mui/material';
import {
  SmartToy,
  Home,
  AdminPanelSettings,
  ExpandMore,
  ContentCopy,
  Memory,
  Build as ToolIcon,
  LightbulbOutlined as ImproveIcon,
  ErrorOutline,
  Refresh,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useCurrentUser } from '../../hooks/useApi';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';
import { ImproveMessageModal } from '../../components/ImproveMessageModal';

interface LlmActivity {
  id: number;
  modelId: number;
  modelName: string;
  provider: string;
  systemPrompt: string | null;
  prompt: string;
  responseBody: string | null;
  toolUseRequest: string | null;
  status: string;
  errorMessage: string | null;
  promptTokens: number | null;
  completionTokens: number | null;
  totalTokens: number | null;
  durationMs: number | null;
  estimatedCostUsd: number | null;
}

interface ChatStep {
  id: number;
  toolUse: string;
  toolQuery: any;
  toolResult: any;
  timestamp: string;
  llmActivityId: number | null;
  estimatedCostUsd: number | null;
  llmActivity?: LlmActivity;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  message: string;
  searchResults: any;
  modelName: string;
  timestamp: string;
  steps: ChatStep[];
  errorOccurred?: boolean;
  errorDetails?: string;
}

interface SessionDetail {
  sessionId: string;
  userId: string;
  userEmail: string;
  userName: string;
  firstMessageTime: string;
  lastActivity: string;
  messageCount: number;
  messages: ChatMessage[];
}

export const MiaSessionDetailPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { data: user, isLoading: isLoadingUser } = useCurrentUser();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const navigate = useNavigate();

  // UI state
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [expandedToolSections, setExpandedToolSections] = useState<Set<string>>(new Set());
  const [sessionIdCopied, setSessionIdCopied] = useState(false);
  const [costBreakdownExpanded, setCostBreakdownExpanded] = useState(false);

  // Improve Mia modal state
  const [improveModalOpen, setImproveModalOpen] = useState(false);
  const [improveMessageId, setImproveMessageId] = useState<string>('');
  const [improveMessageContent, setImproveMessageContent] = useState<string>('');

  // Fetch session detail
  const { data: sessionDetailData, isLoading: isLoadingDetail, refetch } = useQuery({
    queryKey: ['mia-session-detail', sessionId],
    queryFn: async () => {
      if (!sessionId) return null;
      const response = await authenticatedFetch(
        `${API_URL}/api/mia-oversight/admin/sessions/${sessionId}`
      );
      if (!response || !response.ok) {
        throw new Error('Failed to fetch session detail');
      }
      return await response.json();
    },
    enabled: !!sessionId && !!user?.permissions?.includes('market_compass.admin'),
    refetchInterval: 5000, // Poll every 5 seconds
    refetchIntervalInBackground: false,
  });

  // Show loading spinner while user data is being fetched
  if (isLoadingUser) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Redirect if user doesn't have permission
  if (!user?.permissions?.includes('market_compass.admin')) {
    return <Navigate to="/" replace />;
  }

  const sessionDetail: SessionDetail | null = sessionDetailData?.data || null;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const toggleStepsExpanded = (messageId: string) => {
    setExpandedSteps((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const toggleToolSection = (sectionKey: string) => {
    setExpandedToolSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionKey)) {
        newSet.delete(sectionKey);
      } else {
        newSet.add(sectionKey);
      }
      return newSet;
    });
  };

  const handleCopySessionId = async (sessionId: string) => {
    try {
      await navigator.clipboard.writeText(sessionId);
      setSessionIdCopied(true);
      setTimeout(() => setSessionIdCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy session ID:', err);
    }
  };

  const handleImproveMessage = (messageId: string, messageContent: string) => {
    setImproveMessageId(messageId);
    setImproveMessageContent(messageContent);
    setImproveModalOpen(true);
  };

  const calculateTotalCost = () => {
    if (!sessionDetail) return 0;
    return sessionDetail.messages.reduce((sum, msg) => {
      return sum + msg.steps.reduce((stepSum, step) => {
        // Use only step cost from mobile_chat_history_steps table
        // This includes both LLM costs and tool costs (e.g., Perplexity)
        const stepCost = step.estimatedCostUsd || 0;
        return stepSum + stepCost;
      }, 0);
    }, 0);
  };

  const calculateCostByTool = () => {
    if (!sessionDetail) return {};
    const costByTool: { [toolName: string]: number } = {};

    sessionDetail.messages.forEach((msg) => {
      msg.steps.forEach((step) => {
        // Use model name for LLM calls, otherwise use tool name
        let toolName = step.toolUse || 'Unknown';
        if (toolName === 'Claude LLM' && step.llmActivity?.modelName) {
          toolName = step.llmActivity.modelName;
        }
        const stepCost = step.estimatedCostUsd || 0;
        costByTool[toolName] = (costByTool[toolName] || 0) + stepCost;
      });
    });

    return costByTool;
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/hub"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub');
            }}
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            Admin Hub
          </Link>
          <Link
            color="inherit"
            href="/admin/mia-oversight"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/mia-oversight');
            }}
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            <SmartToy sx={{ mr: 0.5 }} fontSize="inherit" />
            Mia Oversight
          </Link>
          <Typography color="text.primary">
            Session Details
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SmartToy sx={{ fontSize: 48, color: '#F5A800' }} />
            <Box>
              <Typography variant="h4" component="h1" sx={{ color: '#607D8B' }}>
                Chat Session Details
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {sessionDetail?.userEmail || sessionId}
              </Typography>
            </Box>
          </Box>
          <Tooltip title="Refresh">
            <IconButton
              onClick={() => refetch()}
              sx={{
                color: '#607D8B',
                '&:hover': {
                  bgcolor: '#F8F9FA'
                }
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>

        {isLoadingDetail ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : sessionDetail ? (
          <Box>
            {/* Session Metadata */}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#607D8B', mb: 2 }}>
                Session Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    User
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {sessionDetail.userEmail}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Messages
                  </Typography>
                  <Typography variant="body2">
                    {sessionDetail.messageCount}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Started
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(sessionDetail.firstMessageTime)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Last Activity
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(sessionDetail.lastActivity)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Estimated Cost
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8
                      }
                    }}
                    onClick={() => setCostBreakdownExpanded(!costBreakdownExpanded)}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#F5A800' }}>
                      ${calculateTotalCost().toFixed(6)}
                    </Typography>
                    <IconButton
                      size="small"
                      sx={{
                        p: 0,
                        color: '#F5A800',
                        '&:hover': {
                          bgcolor: 'transparent'
                        }
                      }}
                    >
                      {costBreakdownExpanded ? (
                        <KeyboardArrowUp fontSize="small" />
                      ) : (
                        <KeyboardArrowDown fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                  <Collapse in={costBreakdownExpanded}>
                    <Paper
                      variant="outlined"
                      sx={{
                        mt: 1,
                        p: 1.5,
                        bgcolor: '#FFFBF0',
                        borderColor: '#FFE0B2',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: '#607D8B',
                          display: 'block',
                          mb: 1
                        }}
                      >
                        Cost Breakdown by Tool
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {Object.entries(calculateCostByTool())
                          .sort(([, costA], [, costB]) => costB - costA)
                          .map(([toolName, cost]) => (
                            <Box
                              key={toolName}
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                py: 0.25,
                                borderBottom: '1px solid #FFE0B2',
                                '&:last-child': {
                                  borderBottom: 'none'
                                }
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ fontSize: '0.75rem', color: '#607D8B' }}
                              >
                                {toolName}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#F5A800' }}
                              >
                                ${cost.toFixed(6)}
                              </Typography>
                            </Box>
                          ))}
                      </Box>
                    </Paper>
                  </Collapse>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Session ID
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      {sessionDetail.sessionId.substring(0, 8)}...
                    </Typography>
                    <Tooltip title={sessionIdCopied ? 'Copied!' : 'Copy to clipboard'}>
                      <IconButton
                        size="small"
                        onClick={() => handleCopySessionId(sessionDetail.sessionId)}
                        sx={{
                          color: sessionIdCopied ? '#4CAF50' : '#607D8B',
                          '&:hover': {
                            bgcolor: sessionIdCopied ? 'rgba(76, 175, 80, 0.1)' : 'rgba(96, 125, 139, 0.1)'
                          }
                        }}
                      >
                        <ContentCopy sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Messages */}
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#607D8B', mb: 2 }}>
                Conversation History
              </Typography>
              <Box>
                {sessionDetail.messages.map((message, index) => (
                  <Box key={message.id}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                        mb: 1,
                      }}
                    >
                      <Paper
                        elevation={1}
                        sx={{
                          px: 2,
                          py: 1.5,
                          maxWidth: message.role === 'user' ? '75%' : 'calc(100% - 20px)',
                          backgroundColor:
                            message.role === 'user'
                              ? '#F5A800'
                              : '#F8F9FA',
                          color: message.role === 'user' ? '#FFFFFF' : '#32373C',
                          border: message.role === 'assistant' ? '1px solid #F5A800' : 'none',
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          sx={{
                            color: message.role === 'user' ? '#FFFFFF' : 'inherit',
                            display: 'block',
                            mb: 0.5
                          }}
                        >
                          {message.role === 'user' ? 'User' : 'Mia'}
                        </Typography>
                        <Box
                          sx={{
                            fontSize: '0.875rem',
                            mb: 1,
                            '& p': {
                              margin: '0.5em 0',
                              '&:first-of-type': { marginTop: 0 },
                              '&:last-child': { marginBottom: 0 }
                            },
                            '& ul, & ol': {
                              margin: '0.5em 0',
                              paddingLeft: '1.5em'
                            },
                            '& li': {
                              margin: '0.25em 0'
                            },
                            '& code': {
                              backgroundColor: message.role === 'user' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.08)',
                              padding: '0.1em 0.3em',
                              borderRadius: '3px',
                              fontSize: '0.9em',
                              fontFamily: 'monospace'
                            },
                            '& pre': {
                              backgroundColor: message.role === 'user' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.08)',
                              padding: '0.75em',
                              borderRadius: '4px',
                              overflow: 'auto',
                              margin: '0.5em 0'
                            },
                            '& pre code': {
                              backgroundColor: 'transparent',
                              padding: 0
                            },
                            '& > *': {
                              maxWidth: '100%'
                            }
                          }}
                        >
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              img: ({ node, ...props }) => (
                                <img
                                  {...props}
                                  style={{
                                    maxWidth: '100%',
                                    height: 'auto',
                                    display: 'block',
                                  }}
                                />
                              ),
                              table: ({ node, ...props }) => (
                                <Box
                                  sx={{
                                    maxWidth: '100%',
                                    maxHeight: '400px',
                                    overflow: 'auto',
                                    margin: '0.5em 0',
                                    border: '1px solid rgba(0, 0, 0, 0.2)',
                                    borderRadius: '4px',
                                    backgroundColor: 'white',
                                  }}
                                >
                                  <table
                                    {...props}
                                    style={{
                                      borderCollapse: 'collapse',
                                      width: '100%',
                                      fontSize: '0.85em',
                                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                    }}
                                  />
                                </Box>
                              ),
                              th: ({ node, ...props }) => (
                                <th
                                  {...props}
                                  style={{
                                    border: '1px solid rgba(0, 0, 0, 0.15)',
                                    padding: '8px 12px',
                                    textAlign: 'left',
                                    whiteSpace: 'nowrap',
                                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                    fontWeight: 600,
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 1,
                                  }}
                                />
                              ),
                              td: ({ node, ...props }) => (
                                <td
                                  {...props}
                                  style={{
                                    border: '1px solid rgba(0, 0, 0, 0.15)',
                                    padding: '8px 12px',
                                    textAlign: 'left',
                                    whiteSpace: 'nowrap',
                                  }}
                                />
                              ),
                              tr: ({ node, ...props }) => {
                                const isEven = props.children && Array.isArray(props.children)
                                  ? (props.children.filter((c: any) => c?.type === 'td').length > 0)
                                  : false;
                                return (
                                  <tr
                                    {...props}
                                    style={{
                                      backgroundColor: isEven ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
                                    }}
                                    onMouseEnter={(e) => {
                                      (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(245, 168, 0, 0.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                      (e.currentTarget as HTMLElement).style.backgroundColor = isEven ? 'rgba(0, 0, 0, 0.02)' : 'transparent';
                                    }}
                                  />
                                );
                              },
                            }}
                          >
                            {message.message}
                          </ReactMarkdown>
                        </Box>
                        {message.errorOccurred && message.errorDetails && (
                          <Alert severity="error" sx={{ my: 1, py: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                              Error Details:
                            </Typography>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1,
                                mt: 0.5,
                                bgcolor: '#FFEBEE',
                                borderColor: '#EF5350',
                                maxHeight: 200,
                                overflowY: 'auto',
                              }}
                            >
                              <Typography
                                variant="caption"
                                component="pre"
                                sx={{
                                  fontFamily: 'monospace',
                                  fontSize: '0.7rem',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  margin: 0,
                                }}
                              >
                                {message.errorDetails}
                              </Typography>
                            </Paper>
                          </Alert>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 1 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {message.modelName && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: message.role === 'user' ? 'rgba(255, 255, 255, 0.8)' : 'text.secondary'
                                }}
                              >
                                Model: {message.modelName}
                              </Typography>
                            )}
                            <Typography
                              variant="caption"
                              sx={{
                                color: message.role === 'user' ? 'rgba(255, 255, 255, 0.8)' : 'text.secondary'
                              }}
                            >
                              {formatDate(message.timestamp)}
                            </Typography>
                          </Box>
                          {message.role === 'assistant' && (
                            <Button
                              size="small"
                              startIcon={<ImproveIcon fontSize="small" />}
                              onClick={() => handleImproveMessage(message.id, message.message)}
                              sx={{
                                fontSize: '0.7rem',
                                textTransform: 'none',
                                color: 'rgba(0, 0, 0, 0.6)',
                                minWidth: 'auto',
                                px: 1,
                                py: 0.25,
                                '&:hover': {
                                  backgroundColor: 'rgba(245, 168, 0, 0.1)',
                                  color: '#F5A800',
                                },
                              }}
                            >
                              Improve Mia
                            </Button>
                          )}
                        </Box>

                        {/* Tool Usage inside Paper */}
                        {message.role === 'assistant' && message.steps && message.steps.length > 0 && (
                          <Accordion
                          expanded={expandedSteps.has(message.id)}
                          onChange={() => toggleStepsExpanded(message.id)}
                          sx={{
                            mt: 2,
                            bgcolor: '#FFF9E6',
                            border: '1px solid #F5A800',
                            boxShadow: 'none',
                            '&:before': { display: 'none' },
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMore sx={{ color: '#F5A800' }} />}
                            sx={{
                              minHeight: 36,
                              '& .MuiAccordionSummary-content': {
                                my: 0.5,
                                alignItems: 'center',
                              },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <ToolIcon sx={{ fontSize: 18, color: '#F5A800' }} />
                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#607D8B' }}>
                                Tool Usage ({message.steps.length} step{message.steps.length !== 1 ? 's' : ''})
                              </Typography>
                              {/* Show total cost for this message's steps */}
                              <Typography variant="caption" sx={{ ml: 'auto', fontWeight: 600, color: '#F5A800' }}>
                                ${message.steps.reduce((sum, step) => {
                                  // Use only step cost to avoid double counting
                                  const stepCost = step.estimatedCostUsd || 0;
                                  return sum + stepCost;
                                }, 0).toFixed(6)}
                              </Typography>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              {message.steps.map((step, stepIndex) => {
                                const queryKey = `${step.id}-query`;
                                const resultKey = `${step.id}-result`;
                                const llmActivityKey = `${step.id}-llm-activity`;

                                return (
                                  <Paper
                                    key={step.id}
                                    variant="outlined"
                                    sx={{
                                      p: 1.5,
                                      bgcolor: '#FFFFFF',
                                      borderColor: '#E0E0E0',
                                    }}
                                  >
                                    {/* Step Header - Always Visible */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                      <Chip
                                        label={`#${stepIndex + 1}`}
                                        size="small"
                                        sx={{
                                          bgcolor: '#607D8B',
                                          color: '#FFFFFF',
                                          fontWeight: 600,
                                          fontSize: '0.7rem',
                                          height: 20,
                                        }}
                                      />
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#003F9F' }}>
                                        {step.toolUse === 'Claude LLM' && step.llmActivity?.modelName
                                          ? step.llmActivity.modelName
                                          : step.toolUse}
                                      </Typography>
                                      {step.estimatedCostUsd && step.estimatedCostUsd > 0 && (
                                        <Chip
                                          label={`$${step.estimatedCostUsd.toFixed(6)}`}
                                          size="small"
                                          sx={{
                                            bgcolor: '#F5A800',
                                            color: '#FFFFFF',
                                            fontWeight: 600,
                                            fontSize: '0.7rem',
                                            height: 20,
                                          }}
                                        />
                                      )}
                                      <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary' }}>
                                        {formatDate(step.timestamp)}
                                      </Typography>
                                    </Box>

                                    {/* Query Accordion */}
                                    {step.toolQuery && (
                                      <Accordion
                                        expanded={expandedToolSections.has(queryKey)}
                                        onChange={() => toggleToolSection(queryKey)}
                                        sx={{
                                          mb: 1,
                                          boxShadow: 'none',
                                          border: '1px solid #E0E0E0',
                                          '&:before': { display: 'none' },
                                        }}
                                      >
                                        <AccordionSummary
                                          expandIcon={<ExpandMore sx={{ fontSize: 18 }} />}
                                          sx={{
                                            minHeight: 32,
                                            bgcolor: '#F8F9FA',
                                            '& .MuiAccordionSummary-content': {
                                              my: 0.5,
                                            },
                                          }}
                                        >
                                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#607D8B' }}>
                                            Query
                                          </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 1, bgcolor: '#FFFFFF' }}>
                                          <Paper
                                            variant="outlined"
                                            sx={{
                                              p: 1,
                                              bgcolor: '#F8F9FA',
                                              borderColor: '#E0E0E0',
                                              maxHeight: 300,
                                              overflowY: 'auto',
                                            }}
                                          >
                                            <Typography
                                              variant="caption"
                                              component="pre"
                                              sx={{
                                                fontFamily: 'monospace',
                                                fontSize: '0.7rem',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                                margin: 0,
                                              }}
                                            >
                                              {JSON.stringify(step.toolQuery, null, 2)}
                                            </Typography>
                                          </Paper>
                                        </AccordionDetails>
                                      </Accordion>
                                    )}

                                    {/* Result Accordion */}
                                    {step.toolResult && (
                                      <Accordion
                                        expanded={expandedToolSections.has(resultKey)}
                                        onChange={() => toggleToolSection(resultKey)}
                                        sx={{
                                          mb: 1,
                                          boxShadow: 'none',
                                          border: '1px solid #E0E0E0',
                                          '&:before': { display: 'none' },
                                        }}
                                      >
                                        <AccordionSummary
                                          expandIcon={<ExpandMore sx={{ fontSize: 18 }} />}
                                          sx={{
                                            minHeight: 32,
                                            bgcolor: '#F8F9FA',
                                            '& .MuiAccordionSummary-content': {
                                              my: 0.5,
                                            },
                                          }}
                                        >
                                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#607D8B' }}>
                                            Result
                                          </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 1, bgcolor: '#FFFFFF' }}>
                                          <Paper
                                            variant="outlined"
                                            sx={{
                                              p: 1,
                                              bgcolor: '#F8F9FA',
                                              borderColor: '#E0E0E0',
                                              maxHeight: 300,
                                              overflowY: 'auto',
                                            }}
                                          >
                                            <Typography
                                              variant="caption"
                                              component="pre"
                                              sx={{
                                                fontFamily: 'monospace',
                                                fontSize: '0.7rem',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                                margin: 0,
                                              }}
                                            >
                                              {JSON.stringify(step.toolResult, null, 2)}
                                            </Typography>
                                          </Paper>
                                        </AccordionDetails>
                                      </Accordion>
                                    )}

                                    {/* LLM Activity Accordion */}
                                    {step.llmActivity && (
                                      <Accordion
                                        expanded={expandedToolSections.has(llmActivityKey)}
                                        onChange={() => toggleToolSection(llmActivityKey)}
                                        sx={{
                                          boxShadow: 'none',
                                          border: '1px solid #E1BEE7',
                                          '&:before': { display: 'none' },
                                        }}
                                      >
                                        <AccordionSummary
                                          expandIcon={<ExpandMore sx={{ fontSize: 18 }} />}
                                          sx={{
                                            minHeight: 32,
                                            bgcolor: '#F3E5F5',
                                            '& .MuiAccordionSummary-content': {
                                              my: 0.5,
                                              alignItems: 'center',
                                              gap: 1,
                                            },
                                          }}
                                        >
                                          <Memory sx={{ fontSize: 16, color: '#9C27B0' }} />
                                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#9C27B0' }}>
                                            LLM Activity
                                          </Typography>
                                          <Chip
                                            label={step.llmActivity.status}
                                            size="small"
                                            color={step.llmActivity.status === 'completed' ? 'success' : step.llmActivity.status === 'failed' ? 'error' : 'default'}
                                            sx={{ height: 18, fontSize: '0.65rem', ml: 'auto' }}
                                          />
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 1.5, bgcolor: '#FFFFFF' }}>
                                          <Grid container spacing={1} sx={{ mb: 1.5 }}>
                                            <Grid item xs={6}>
                                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                                Model:
                                              </Typography>
                                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                {step.llmActivity.modelName} ({step.llmActivity.provider})
                                              </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                                Duration:
                                              </Typography>
                                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                {step.llmActivity.durationMs ? `${step.llmActivity.durationMs}ms` : 'N/A'}
                                              </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                                Tokens:
                                              </Typography>
                                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                {step.llmActivity.promptTokens || 0} + {step.llmActivity.completionTokens || 0} = {step.llmActivity.totalTokens || 0}
                                              </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                                Cost (ref):
                                              </Typography>
                                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#607D8B' }}>
                                                ${step.llmActivity.estimatedCostUsd?.toFixed(4) || '0.0000'}
                                              </Typography>
                                              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.65rem', fontStyle: 'italic' }}>
                                                (included in step cost)
                                              </Typography>
                                            </Grid>
                                          </Grid>

                                          {step.llmActivity.errorMessage && (
                                            <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
                                              <Typography variant="caption">
                                                {step.llmActivity.errorMessage}
                                              </Typography>
                                            </Alert>
                                          )}

                                          {step.llmActivity.systemPrompt && (
                                            <Box sx={{ mb: 1 }}>
                                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#607D8B', display: 'block', mb: 0.5 }}>
                                                System Prompt:
                                              </Typography>
                                              <Paper
                                                variant="outlined"
                                                sx={{
                                                  p: 1,
                                                  bgcolor: '#F3E5F5',
                                                  borderColor: '#E1BEE7',
                                                  maxHeight: 150,
                                                  overflowY: 'auto',
                                                }}
                                              >
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    fontSize: '0.7rem',
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                  }}
                                                >
                                                  {step.llmActivity.systemPrompt}
                                                </Typography>
                                              </Paper>
                                            </Box>
                                          )}

                                          <Box sx={{ mb: 1 }}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#607D8B', display: 'block', mb: 0.5 }}>
                                              Prompt:
                                            </Typography>
                                            <Paper
                                              variant="outlined"
                                              sx={{
                                                p: 1,
                                                bgcolor: '#F3E5F5',
                                                borderColor: '#E1BEE7',
                                                maxHeight: 150,
                                                overflowY: 'auto',
                                              }}
                                            >
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  fontSize: '0.7rem',
                                                  whiteSpace: 'pre-wrap',
                                                  wordBreak: 'break-word',
                                                }}
                                              >
                                                {step.llmActivity.prompt}
                                              </Typography>
                                            </Paper>
                                          </Box>

                                          {step.llmActivity.responseBody && (
                                            <Box sx={{ mb: 1 }}>
                                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#607D8B', display: 'block', mb: 0.5 }}>
                                                Response:
                                              </Typography>
                                              <Paper
                                                variant="outlined"
                                                sx={{
                                                  p: 1,
                                                  bgcolor: '#F3E5F5',
                                                  borderColor: '#E1BEE7',
                                                  maxHeight: 150,
                                                  overflowY: 'auto',
                                                }}
                                              >
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    fontSize: '0.7rem',
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                  }}
                                                >
                                                  {step.llmActivity.responseBody}
                                                </Typography>
                                              </Paper>
                                            </Box>
                                          )}

                                          {step.llmActivity.toolUseRequest && (
                                            <Box>
                                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#F5A800', display: 'block', mb: 0.5 }}>
                                                Tool Request:
                                              </Typography>
                                              <Paper
                                                variant="outlined"
                                                sx={{
                                                  p: 1,
                                                  bgcolor: '#FFFBF0',
                                                  borderColor: '#FFE0B2',
                                                  maxHeight: 300,
                                                  overflowY: 'auto',
                                                }}
                                              >
                                                <Typography
                                                  variant="caption"
                                                  component="pre"
                                                  sx={{
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.7rem',
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                    margin: 0,
                                                  }}
                                                >
                                                  {JSON.stringify(JSON.parse(step.llmActivity.toolUseRequest), null, 2)}
                                                </Typography>
                                              </Paper>
                                            </Box>
                                          )}
                                        </AccordionDetails>
                                      </Accordion>
                                    )}
                                  </Paper>
                                );
                              })}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                        )}
                      </Paper>
                    </Box>

                    {index < sessionDetail.messages.length - 1 && <Divider sx={{ my: 2 }} />}
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>
        ) : (
          <Alert severity="error">Failed to load session details</Alert>
        )}
      </Box>

      {/* Improve Mia Modal */}
      <ImproveMessageModal
        open={improveModalOpen}
        onClose={() => setImproveModalOpen(false)}
        messageId={improveMessageId}
        messageContent={improveMessageContent}
      />
    </Container>
  );
};
